import BackButton from "../components/BackButton";
import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Users, Star, Zap, Heart } from 'lucide-react';

export default function AboutPage({ onBack }) {
  const [appInfo, setAppInfo] = useState(null);

  useEffect(() => {
    api.get('/app/info').then(res => setAppInfo(res.data)).catch(() => {});
  }, []);

  return (
    <div className="p-4" data-testid="about-page">
      {onBack && <BackButton onClick={onBack} />}

      {/* Hero — v5.7.54: brand app-icon (replaces the 3D globe badge so
          the About screen matches the iOS/Android home-screen icon). */}
      <div className="text-center mb-6">
        <div className="mb-3 flex justify-center" data-testid="about-app-icon">
          <img
            src="/icon-512.png"
            alt="Fantasy World Star app icon"
            className="rounded-3xl shadow-xl"
            style={{ width: 112, height: 112 }}
          />
        </div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
          Fantasy World Star
        </h1>
        <p className="text-sm text-muted-foreground mt-1" data-testid="about-subtitle">
          Draft the world. Score the headlines.
        </p>
        <Badge variant="secondary" className="mt-2" data-testid="about-version">
          v{appInfo?.version || '—'}
        </Badge>
      </div>

      {/* Description */}
      <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4">
        <p className="text-sm text-foreground/80 leading-relaxed">
          {appInfo?.description || 'Draft teams of real-world people and earn Fantasy Dollars (₣) based on real-world events.'}
        </p>
      </div>

      {/* How the market works — owner request 2026-08-12: explain that prices
          track rarity/demand and that events pay out like stock dividends.
          Mirrored on mobile AboutScreen. */}
      <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4" data-testid="about-how-it-works">
        <h3 className="text-sm font-semibold mb-2">How the Game Works</h3>
        <div className="space-y-2 text-xs text-muted-foreground leading-relaxed">
          <p>
            <span className="font-medium text-foreground/80">A stock market of fame.</span>{' '}
            Every person has a market price in Fantasy Dollars (₣) driven by rarity and demand —
            the more coveted a star is, the more they cost to draft.
          </p>
          <p>
            <span className="font-medium text-foreground/80">Events pay like dividends.</span>{' '}
            When someone on your roster makes real headlines — a title win, a hit single, a viral
            moment — your team automatically collects ₣, just like a stock paying out to its
            shareholders.
          </p>
          <p>
            <span className="font-medium text-foreground/80">Buy low, ride the rise.</span>{' '}
            Prices move with real-world attention and player demand. Spot a rising star early,
            draft them cheap, and watch your portfolio grow.
          </p>
        </div>
      </div>

      {/* Live stats — values come from current DB; backend refreshes every 24h */}
      {appInfo?.stats && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            { icon: Users, label: 'People', value: appInfo.stats.entities, testId: 'about-stat-people' },
            { icon: Star, label: 'Users', value: appInfo.stats.users, testId: 'about-stat-users' },
            { icon: Zap, label: 'Events', value: appInfo.stats.events_processed, testId: 'about-stat-events' },
          ].map(({ icon: Icon, label, value, testId }, i) => (
            <div key={i} className="bg-card border border-border/60 rounded-xl p-3 text-center" data-testid={testId}>
              <Icon className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
              <p className="text-lg font-bold tabular-nums">{value?.toLocaleString() || 0}</p>
              <p className="text-[10px] text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Legal */}
      <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4">
        <h3 className="text-sm font-semibold mb-2">Legal & Compliance</h3>
        <div className="space-y-1 text-xs text-muted-foreground">
          <p>No gambling • No prizes • No sweepstakes • No pay-to-win</p>
          <p>Objective public-event scoring • AI-stylized art (not real likenesses)</p>
          <p>Fantasy Dollars have no cash value • Adults 18+</p>
          <p>Designed to support GDPR and CCPA/CPRA rights • Built to WCAG 2.1 guidelines</p>
          <p>
            Are you shown here?{' '}
            <a href="/legal/takedown" className="underline" data-testid="about-takedown-link">Request removal</a>
          </p>
        </div>
      </div>

      {/* Data & News Sources — TVmaze attribution is REQUIRED (its data is
          CC BY-SA); the rest are courtesy credits. Mirrored on mobile
          AboutScreen. */}
      <div className="bg-card border border-border/60 rounded-2xl p-4 mb-4" data-testid="about-data-sources">
        <h3 className="text-sm font-semibold mb-2">Data & News Sources</h3>
        <div className="space-y-1 text-xs text-muted-foreground">
          <p>
            TV schedule data from{' '}
            <a href="https://www.tvmaze.com" target="_blank" rel="noopener noreferrer" className="underline" data-testid="about-tvmaze-link">TVmaze.com</a>{' '}
            (CC BY-SA)
          </p>
          <p>
            Trending data &amp; photos from{' '}
            <a href="https://www.wikipedia.org" target="_blank" rel="noopener noreferrer" className="underline">Wikipedia</a>{' '}
            — see <a href="/credits" className="underline" data-testid="about-photo-credits-link">photo credits</a>
          </p>
          <p>
            News headlines via{' '}
            <a href="https://newsdata.io" target="_blank" rel="noopener noreferrer" className="underline">NewsData.io</a>,{' '}
            <a href="https://gnews.io" target="_blank" rel="noopener noreferrer" className="underline">GNews</a>{' '}
            and public RSS feeds
          </p>
          <p>
            Avatars by{' '}
            <a href="https://dicebear.com" target="_blank" rel="noopener noreferrer" className="underline">DiceBear</a>
          </p>
        </div>
      </div>

      <div className="text-center text-xs text-muted-foreground mt-6">
        {/* v5.7.88 — Gazumba Games company mark. Sits above the credit
            line so the brand is anchored visually. Black-and-white PNG;
            no `filter: invert` here (unlike the splash) because the
            About card is on a light surface — the natural greyscale
            reads correctly. */}
        <img
          src="/gazumba-games-logo.png"
          alt="Gazumba Games"
          width={56}
          height={56}
          className="mx-auto mb-2 opacity-90"
          data-testid="about-gazumba-logo"
        />
        <p className="flex items-center justify-center gap-1">
          Made with <Heart className="h-3 w-3 text-red-400" /> by Gazumba Games
        </p>
        <p className="mt-1">© 2026 Gazumba Games. All Rights Reserved. Fantasy World Star™</p>
      </div>
    </div>
  );
}
